import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Trash2, Edit, Save, X, ShieldCheck, Users, MessageSquare, Film,
  Upload, Image as ImageIcon, Tag, Eye, ThumbsUp, Compass
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { v: "patreon_exclusive", label: "Patreon Exclusive (only on Patreon)" },
  { v: "unlock_in_progress", label: "Unlock In Progress (on YouTube, like to unlock)" },
  { v: "unlocked", label: "Unlocked (free for everyone)" },
];

const emptyVideo = {
  title: "", description: "", route_id: "", thumbnail: "",
  youtube_url: "", patreon_url: "", external_url: "",
  status: "patreon_exclusive", like_goal: 1000, tags: [],
};
const emptyRoute = { id: "", name: "", description: "", character_image: "", order: 99 };

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("videos");
  const [videos, setVideos] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]);

  // Forms
  const [editingV, setEditingV] = useState(null);
  const [formV, setFormV] = useState(emptyVideo);
  const [editingR, setEditingR] = useState(null);
  const [formR, setFormR] = useState(emptyRoute);

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/login");
      else if (user.role !== "admin") navigate("/");
    }
  }, [user, loading, navigate]);

  const loadAll = async () => {
    try {
      const [v, r, u, c] = await Promise.all([
        api.get("/videos", { params: { limit: 500 } }),
        api.get("/routes"),
        api.get("/admin/users"),
        api.get("/admin/comments"),
      ]);
      setVideos(v.data); setRoutes(r.data); setUsers(u.data); setComments(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (user?.role === "admin") loadAll(); }, [user]);

  // ===== Videos =====
  const openNewV = () => {
    setEditingV("new");
    setFormV({ ...emptyVideo, route_id: routes[0]?.id || "" });
  };
  const openEditV = (v) => { setEditingV(v.id); setFormV({ ...emptyVideo, ...v, tags: v.tags || [] }); };
  const cancelV = () => { setEditingV(null); setFormV(emptyVideo); };

  const submitV = async () => {
    try {
      const payload = { ...formV, like_goal: Number(formV.like_goal) || 0 };
      if (editingV === "new") {
        if (!payload.route_id) { toast.error("Pick a route"); return; }
        await api.post("/admin/videos", payload);
        toast.success("Video created");
      } else {
        await api.put(`/admin/videos/${editingV}`, payload);
        toast.success("Video updated");
      }
      cancelV(); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeV = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try { await api.delete(`/admin/videos/${id}`); toast.success("Deleted"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ===== Routes =====
  const openNewR = () => { setEditingR("new"); setFormR(emptyRoute); };
  const openEditR = (r) => { setEditingR(r.id); setFormR({ ...emptyRoute, ...r }); };
  const cancelR = () => { setEditingR(null); setFormR(emptyRoute); };

  const submitR = async () => {
    try {
      if (editingR === "new") {
        if (!formR.id || !formR.name) { toast.error("ID and name required"); return; }
        await api.post("/admin/routes", { ...formR, order: Number(formR.order) || 99 });
        toast.success("Route created");
      } else {
        await api.put(`/admin/routes/${editingR}`, {
          name: formR.name, description: formR.description,
          character_image: formR.character_image, order: Number(formR.order) || 99,
        });
        toast.success("Route updated");
      }
      cancelR(); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeR = async (id) => {
    if (!window.confirm(`Delete route "${id}"?`)) return;
    try { await api.delete(`/admin/routes/${id}`); toast.success("Deleted"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ===== Users & comments =====
  const removeUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("User deleted"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeComment = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try { await api.delete(`/comments/${id}`); toast.success("Comment deleted"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user || user.role !== "admin") return null;

  const TABS = [
    { k: "videos", label: "Videos", Icon: Film, count: videos.length },
    { k: "routes", label: "Routes", Icon: Compass, count: routes.length },
    { k: "users", label: "Users", Icon: Users, count: users.length },
    { k: "comments", label: "Comments", Icon: MessageSquare, count: comments.length },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#E50914] font-bold inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Admin
          </span>
          <h1 className="font-display text-4xl font-black mt-2">Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          <a href="/admin/stats" data-testid="admin-stats-link" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm">
            <Eye className="w-4 h-4" /> Statistics
          </a>
          <a href="/admin/banners" data-testid="admin-banners-link" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm">
            <ShieldCheck className="w-4 h-4" /> Banners
          </a>
          {tab === "videos" && (
            <button data-testid="admin-new-video" onClick={openNewV} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white font-bold">
              <Plus className="w-4 h-4" /> New Video
            </button>
          )}
          {tab === "routes" && (
            <button data-testid="admin-new-route" onClick={openNewR} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white font-bold">
              <Plus className="w-4 h-4" /> New Route
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-white/10 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`admin-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-3 text-sm font-bold uppercase tracking-widest flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
              tab === t.k ? "border-[#E50914] text-white" : "border-transparent text-zinc-500 hover:text-white"
            }`}
          >
            <t.Icon className="w-4 h-4" /> {t.label}
            <span className="ml-1 px-1.5 py-0.5 rounded bg-white/5 text-[10px]">{t.count}</span>
          </button>
        ))}
      </div>

      {tab === "videos" && (
        <>
          {editingV && (
            <VideoForm
              form={formV} setForm={setFormV} routes={routes}
              editing={editingV} onCancel={cancelV} onSave={submitV}
            />
          )}
          <div className="glass rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-left">
                <tr>
                  <th className="px-4 py-3 font-bold uppercase tracking-widest text-xs">Title</th>
                  <th className="px-4 py-3 font-bold uppercase tracking-widest text-xs hidden md:table-cell">Route</th>
                  <th className="px-4 py-3 font-bold uppercase tracking-widest text-xs">Status</th>
                  <th className="px-4 py-3 font-bold uppercase tracking-widest text-xs hidden md:table-cell">Stats</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {videos.map((v) => (
                  <tr key={v.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3 font-semibold">{v.title}</td>
                    <td className="px-4 py-3 text-zinc-400 hidden md:table-cell capitalize">{v.route_id}</td>
                    <td className="px-4 py-3"><span className="text-xs font-bold uppercase tracking-widest">{v.status}</span></td>
                    <td className="px-4 py-3 hidden md:table-cell text-zinc-400">
                      <span className="inline-flex items-center gap-1 mr-3"><Eye className="w-3.5 h-3.5" /> {v.views || 0}</span>
                      <span className="inline-flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" /> {v.likes || 0}/{v.like_goal}</span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                      <button data-testid={`edit-${v.id}`} onClick={() => openEditV(v)} className="p-2 rounded-lg hover:bg-white/10 text-zinc-300"><Edit className="w-4 h-4" /></button>
                      <button data-testid={`del-${v.id}`} onClick={() => removeV(v.id)} className="p-2 rounded-lg hover:bg-[#E50914]/20 text-[#E50914]"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {videos.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No videos yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "routes" && (
        <>
          {editingR && (
            <RouteForm form={formR} setForm={setFormR} editing={editingR} onCancel={cancelR} onSave={submitR} />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {routes.map((r) => (
              <div key={r.id} className="glass rounded-2xl overflow-hidden">
                <div className="relative aspect-[16/9]">
                  <img src={r.character_image} alt={r.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-display font-bold text-lg">{r.name}</h4>
                    <span className="text-[10px] text-zinc-500 font-bold">{r.video_count} vids · {r.progress}%</span>
                  </div>
                  <p className="text-xs text-zinc-400 line-clamp-2">{r.description}</p>
                  <div className="flex items-center gap-2 pt-2">
                    <button data-testid={`admin-edit-${r.id}`} onClick={() => openEditR(r)} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-bold">
                      <Edit className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button data-testid={`admin-del-${r.id}`} onClick={() => removeR(r.id)} className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-[#E50914]/10 text-[#E50914] hover:bg-[#E50914] hover:text-white text-xs font-bold">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "users" && (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left">
              <tr>
                <th className="px-4 py-3 font-bold uppercase tracking-widest text-xs">Name</th>
                <th className="px-4 py-3 font-bold uppercase tracking-widest text-xs">Email</th>
                <th className="px-4 py-3 font-bold uppercase tracking-widest text-xs">Role</th>
                <th className="px-4 py-3 font-bold uppercase tracking-widest text-xs hidden md:table-cell">Provider</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-3 font-semibold">{u.name}</td>
                  <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                  <td className="px-4 py-3"><span className={`text-xs uppercase tracking-widest font-bold ${u.role === "admin" ? "text-[#E50914]" : "text-zinc-300"}`}>{u.role}</span></td>
                  <td className="px-4 py-3 text-zinc-500 hidden md:table-cell uppercase text-xs">{u.auth_provider}</td>
                  <td className="px-4 py-3 text-right">
                    {u.id !== user.id && (
                      <button data-testid={`del-user-${u.id}`} onClick={() => removeUser(u.id)} className="p-2 rounded-lg hover:bg-[#E50914]/20 text-[#E50914]"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No users.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "comments" && (
        <div className="space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="glass rounded-2xl p-4 flex items-start gap-3">
              <div className="flex-1">
                <div className="text-xs text-zinc-500">{c.user_name} · {new Date(c.created_at).toLocaleString()} · video <code className="text-zinc-400">{c.video_id}</code></div>
                <p className="text-sm mt-1">{c.text}</p>
              </div>
              <button data-testid={`del-com-${c.id}`} onClick={() => removeComment(c.id)} className="p-2 rounded-lg hover:bg-[#E50914]/20 text-[#E50914]"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {comments.length === 0 && <div className="text-center text-zinc-500 py-10">No comments yet.</div>}
        </div>
      )}
    </div>
  );
}

/* ============ FORMS ============ */

function VideoForm({ form, setForm, routes, editing, onCancel, onSave }) {
  const fileRef = useRef(null);
  const [tagInput, setTagInput] = useState("");

  const onUploadImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image too large (max 2MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, thumbnail: reader.result });
    reader.onerror = () => toast.error("Failed to read file");
    reader.readAsDataURL(file);
  };

  const addTag = (e) => {
    e.preventDefault();
    const t = tagInput.trim().toLowerCase();
    if (!t) return;
    if ((form.tags || []).includes(t)) { setTagInput(""); return; }
    setForm({ ...form, tags: [...(form.tags || []), t] });
    setTagInput("");
  };
  const removeTag = (t) => setForm({ ...form, tags: (form.tags || []).filter((x) => x !== t) });

  return (
    <div className="glass-strong rounded-2xl p-6 mb-6 space-y-4" data-testid="admin-video-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg">{editing === "new" ? "New video" : "Edit video"}</h3>
        <button onClick={onCancel} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      <Field label="Title">
        <input data-testid="f-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" placeholder="Video title" />
      </Field>

      <Field label="Description">
        <textarea data-testid="f-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" placeholder="Tell viewers what this drop is about..." />
      </Field>

      <Field label="Tags">
        <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10">
          {(form.tags || []).map((t) => (
            <span key={t} data-testid={`tag-${t}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#E50914]/10 text-[#E50914] text-xs font-bold uppercase tracking-widest border border-[#E50914]/30">
              <Tag className="w-3 h-3" /> {t}
              <button onClick={() => removeTag(t)} className="hover:text-white ml-0.5"><X className="w-3 h-3" /></button>
            </span>
          ))}
          <input
            data-testid="f-tag-input"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTag(e)}
            placeholder="Type a tag and press Enter"
            className="flex-1 min-w-[140px] bg-transparent outline-none text-sm text-white"
          />
        </div>
        <p className="text-[11px] text-zinc-500 mt-1">Use route names like "debbie" or themes like "romance".</p>
      </Field>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Video Embed (YouTube / Patreon / iframe code)">
          <textarea data-testid="f-yt" rows={2} value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} placeholder='https://youtube.com/watch?v=...  or paste an entire <iframe src="..."></iframe> tag' className="input" />
          <p className="text-[11px] text-zinc-500 mt-1">Paste a YouTube URL OR the full HTML <code>&lt;iframe&gt;</code> embed code from another site (e.g. rule34video, Patreon, etc.).</p>
        </Field>
        <Field label="Patreon link">
          <input data-testid="f-pat" value={form.patreon_url} onChange={(e) => setForm({ ...form, patreon_url: e.target.value })} placeholder="https://patreon.com/..." className="input" />
        </Field>
      </div>

      <Field label="External full-version URL (optional)">
        <input data-testid="f-ext" value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://other-site.com/full-uncensored" className="input" />
        <p className="text-[11px] text-zinc-500 mt-1">If the full uncensored version is hosted on another site, put it here.</p>
      </Field>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Route">
          <select data-testid="f-route" value={form.route_id} onChange={(e) => setForm({ ...form, route_id: e.target.value })} className="input">
            <option value="" className="bg-black">— Select route —</option>
            {routes.map((r) => <option key={r.id} value={r.id} className="bg-black">{r.name}</option>)}
          </select>
        </Field>
        <Field label="Like goal">
          <input data-testid="f-goal" type="number" min={0} value={form.like_goal} onChange={(e) => setForm({ ...form, like_goal: e.target.value })} className="input" />
        </Field>
      </div>

      <Field label="Availability">
        <div className="space-y-2">
          {STATUS_OPTIONS.map((s) => (
            <label key={s.v} data-testid={`f-status-${s.v}`} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.status === s.v ? "border-[#E50914] bg-[#E50914]/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}>
              <input type="radio" name="status" value={s.v} checked={form.status === s.v} onChange={(e) => setForm({ ...form, status: e.target.value })} className="accent-[#E50914]" />
              <span className="text-sm">{s.label}</span>
            </label>
          ))}
        </div>
      </Field>

      <Field label="Thumbnail">
        <div className="flex flex-col md:flex-row gap-3">
          <input data-testid="f-thumb" value={form.thumbnail?.startsWith("data:") ? "" : form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} className="input flex-1" placeholder="Paste an image URL or upload below" />
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUploadImage} data-testid="f-thumb-file" />
          <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold whitespace-nowrap">
            <Upload className="w-4 h-4" /> Upload image
          </button>
        </div>
        {form.thumbnail && (
          <div className="mt-3 relative w-48 aspect-video rounded-lg overflow-hidden border border-white/10">
            <img src={form.thumbnail} alt="" className="w-full h-full object-cover" />
            <button onClick={() => setForm({ ...form, thumbnail: "" })} className="absolute top-1 right-1 p-1 rounded bg-black/70 text-white hover:bg-[#E50914]"><X className="w-3 h-3" /></button>
          </div>
        )}
      </Field>

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300 font-bold text-sm">Cancel</button>
        <button data-testid="admin-save-video" onClick={onSave} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white font-bold text-sm">
          <Save className="w-4 h-4" /> Save
        </button>
      </div>
    </div>
  );
}

function RouteForm({ form, setForm, editing, onCancel, onSave }) {
  const fileRef = useRef(null);
  const onUploadImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image too large (max 2MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, character_image: reader.result });
    reader.readAsDataURL(file);
  };
  return (
    <div className="glass-strong rounded-2xl p-6 mb-6 space-y-4" data-testid="admin-route-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg">{editing === "new" ? "New route" : "Edit route"}</h3>
        <button onClick={onCancel} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="ID (slug)"><input data-testid="rf-id" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} disabled={editing !== "new"} className="input disabled:opacity-60" placeholder="debbie" /></Field>
        <Field label="Display name"><input data-testid="rf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder="Debbie" /></Field>
      </div>
      <Field label="Description"><textarea data-testid="rf-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" /></Field>
      <Field label="Character image">
        <div className="flex flex-col md:flex-row gap-3">
          <input data-testid="rf-image" value={form.character_image?.startsWith("data:") ? "" : form.character_image} onChange={(e) => setForm({ ...form, character_image: e.target.value })} className="input flex-1" placeholder="Image URL or upload" />
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUploadImage} data-testid="rf-image-file" />
          <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold whitespace-nowrap"><ImageIcon className="w-4 h-4" /> Upload</button>
        </div>
        {form.character_image && <img src={form.character_image} alt="" className="mt-3 w-32 aspect-[3/4] object-cover rounded-lg border border-white/10" />}
      </Field>
      <Field label="Order"><input data-testid="rf-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="input w-32" /></Field>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300 font-bold text-sm">Cancel</button>
        <button data-testid="admin-save-route" onClick={onSave} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white font-bold text-sm"><Save className="w-4 h-4" /> Save</button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
